package or.garden.manager.features.planting

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import karika.distribucija.ba.domain.model.ResultState
import or.garden.manager.domain.PlantingService
import or.garden.manager.domain.model.PlantingItem
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

@Composable
fun PlantingScreen() {
    var plantings by remember { mutableStateOf<List<PlantingItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }

    // Get current month
    val initialMonth = remember {
        Date().getMonth() + 1  // getMonth() returns 0-11, we need 1-12
    }
    var selectedMonth by remember { mutableStateOf(initialMonth) }

    val monthNames = listOf(
        "Januar", "Februar", "Mart", "April", "Maj", "Juni",
        "Juli", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"
    )

    LaunchedEffect(selectedMonth) {
        isLoading = true
        PlantingService.getPlantings(selectedMonth) { state ->
            when (state) {
                is ResultState.Success -> {
                    plantings = state.data.data?.data ?: emptyList()
                    isLoading = false
                }
                is ResultState.Error -> {
                    isLoading = false
                    console.log("Error loading plantings: ${state.message}")
                }
                else -> {}
            }
        }
    }

    Div({ classes("max-w-6xl", "mx-auto") }) {
        // Header with month navigation
        Div({ classes("mb-8", "flex", "items-center", "justify-between") }) {
            Div({ classes("flex-1") }) {
                H2({ classes("text-2xl", "font-bold", "text-gray-900", "mb-2") }) {
                    Text("Sadnja - ${monthNames[selectedMonth - 1]}")
                }
                P({ classes("text-gray-500") }) {
                    Text("Preporučene biljke za sadnju ovog mjeseca")
                }
            }

            // Month navigation buttons
            Div({ classes("flex", "items-center", "gap-2") }) {
                // Previous month button
                Button({
                    classes(
                        "w-10", "h-10", "rounded-lg", "bg-emerald-600",
                        "hover:bg-emerald-700", "text-white", "flex",
                        "items-center", "justify-center", "transition-colors"
                    )
                    if (selectedMonth <= 1) {
                        attr("disabled", "true")
                        classes("opacity-50", "cursor-not-allowed")
                    }
                    onClick {
                        if (selectedMonth > 1) {
                            selectedMonth--
                        }
                    }
                }) {
                    I({ classes("fas", "fa-chevron-left") })
                }

                // Current month indicator
                Div({
                    classes(
                        "px-4", "py-2", "bg-gray-100", "rounded-lg",
                        "text-sm", "font-semibold", "text-gray-700"
                    )
                }) {
                    Text(monthNames[selectedMonth - 1])
                }

                // Next month button
                Button({
                    classes(
                        "w-10", "h-10", "rounded-lg", "bg-emerald-600",
                        "hover:bg-emerald-700", "text-white", "flex",
                        "items-center", "justify-center", "transition-colors"
                    )
                    if (selectedMonth >= 12) {
                        attr("disabled", "true")
                        classes("opacity-50", "cursor-not-allowed")
                    }
                    onClick {
                        if (selectedMonth < 12) {
                            selectedMonth++
                        }
                    }
                }) {
                    I({ classes("fas", "fa-chevron-right") })
                }
            }
        }

        // Loading State
        if (isLoading) {
            Div({ classes("flex", "justify-center", "items-center", "py-20") }) {
                I({ classes("fas", "fa-circle-notch", "fa-spin", "text-4xl", "text-emerald-600") })
            }
        } else if (plantings.isEmpty()) {
            // Empty State
            Div({
                classes(
                    "flex",
                    "flex-col",
                    "items-center",
                    "justify-center",
                    "py-20",
                    "text-center"
                )
            }) {
                I({ classes("fas", "fa-seedling", "text-6xl", "text-gray-300", "mb-4") })
                P({ classes("text-xl", "font-semibold", "text-gray-500", "mb-2") }) {
                    Text("Nema podataka o sadnji")
                }
                P({ classes("text-gray-400") }) {
                    Text("Trenutno nema preporučenih biljaka za ovaj mjesec.")
                }
            }
        } else {
            // Plantings Grid
            Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-6") }) {
                plantings.forEach { planting ->
                    PlantingCard(planting)
                }
            }
        }
    }
}

@Composable
private fun PlantingCard(planting: PlantingItem) {
    Div({
        classes(
            "bg-white",
            "rounded-2xl",
            "shadow-sm",
            "border",
            "border-gray-100",
            "p-6",
            "hover:shadow-md",
            "transition-all"
        )
    }) {
        // Vegetable Type
        Div({ classes("mb-4") }) {
            H2({ classes("text-xl", "font-bold", "text-gray-900", "mb-1") }) {
                Text(planting.vegetableType)
            }
            if (!planting.varietySelection.isNullOrBlank()) {
                P({ classes("text-sm", "text-emerald-600", "font-medium") }) {
                    Text(planting.varietySelection)
                }
            }
        }

        // Sowing Date
        Div({ classes("flex", "items-center", "gap-2", "mb-3") }) {
            I({ classes("fas", "fa-calendar-alt", "text-gray-400", "text-sm") })
            Span({ classes("text-sm", "text-gray-600") }) {
                Text(planting.sowingDate)
            }
        }

        // Recommended Seedling Count
        if (!planting.recommendedSeedlingCount.isNullOrBlank()) {
            Div({ classes("flex", "items-center", "gap-2", "mb-3") }) {
                I({ classes("fas", "fa-hashtag", "text-gray-400", "text-sm") })
                Span({ classes("text-sm", "text-gray-600") }) {
                    Text(planting.recommendedSeedlingCount)
                }
            }
        }

        // Direct Planting Badge
        if (planting.directPlanting) {
            Div({
                classes(
                    "inline-flex",
                    "items-center",
                    "gap-2",
                    "px-3",
                    "py-1",
                    "bg-emerald-50",
                    "text-emerald-700",
                    "rounded-full",
                    "text-xs",
                    "font-semibold",
                    "mt-2"
                )
            }) {
                I({ classes("fas", "fa-check-circle") })
                Text("Direktna sadnja")
            }
        }
    }
}

